import { useState, type ReactNode } from 'react';
import { FileSpreadsheet } from 'lucide-react';
import { toast } from 'react-toastify';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  directoryImportError,
  downloadDirectoryImportTemplate,
  mappedRows,
  useCommitDirectoryImport,
  useParseDirectoryFile,
  usePreviewDirectoryImport,
  type DirectoryImportKind,
  type DirectoryImportParse,
  type DirectoryImportPreview,
  type DirectoryImportStatus,
} from 'hooks/hwcgDirectoryImport/hwcgDirectoryImportHooks';

type Step = 'upload' | 'map' | 'review';

export type DirectoryImportField = {
  field: string;
  label: string;
};

const NOT_USED = '__not_used__';
const ACCEPT = '.xls,.xlsx,.csv';
const SAMPLE_ROWS = 5;

const STATUS_LABELS: Record<DirectoryImportStatus, string> = {
  new: 'New',
  update: 'Update',
  invalid: 'Invalid',
  duplicate_in_file: 'Repeat in file',
};

const STATUS_VARIANT: Record<
  DirectoryImportStatus,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  new: 'default',
  update: 'secondary',
  invalid: 'destructive',
  duplicate_in_file: 'outline',
};

/**
 * Spreadsheet importer for the HWCG directory, following the Assets importer:
 * upload, match columns, review every row's outcome, then import.
 */
export function HwcgDirectoryImportDialog({
  open,
  onOpenChange,
  kind,
  title,
  noun,
  nounPlural,
  fields,
  requiredAnyOf,
  uploadExtra,
  constituency,
  rowLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  kind: DirectoryImportKind;
  title: string;
  /** Lowercase: "contact" / "contacts". */
  noun: string;
  nounPlural: string;
  fields: DirectoryImportField[];
  /** Mapping needs at least one of these fields. */
  requiredAnyOf: string[];
  /** Extra controls on the upload step, such as the target list. */
  uploadExtra?: ReactNode;
  constituency?: string;
  /** What the review table shows to name each row. */
  rowLabel: (fields: Record<string, string | boolean | null>) => string;
}) {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [parsed, setParsed] = useState<DirectoryImportParse | null>(null);
  const [sheetName, setSheetName] = useState('');
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<DirectoryImportPreview | null>(null);

  const parse = useParseDirectoryFile(kind);
  const previewImport = usePreviewDirectoryImport(kind);
  const commit = useCommitDirectoryImport(kind);

  const sheet = parsed?.sheets.find((item) => item.name === sheetName);
  const tooManyRows = !!sheet && !!parsed && sheet.rowCount > parsed.maxRows;
  const mappingReady = requiredAnyOf.some((field) => mapping[field]);
  const importCount = preview ? preview.counts.new + preview.counts.update : 0;
  const mappedFields = fields.filter((item) => mapping[item.field]);

  const reset = () => {
    setStep('upload');
    setFile(null);
    setParsed(null);
    setSheetName('');
    setMapping({});
    setPreview(null);
    parse.reset();
    previewImport.reset();
    commit.reset();
  };

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const applySheet = (name: string, result = parsed) => {
    const match = result?.sheets.find((item) => item.name === name);
    if (!match) return;
    setSheetName(match.name);
    setMapping(match.suggestedMapping ?? {});
  };

  const chooseFile = (next: File | undefined) => {
    if (!next) return;
    setFile(next);
    setParsed(null);
    parse.mutate(next, {
      onSuccess: (result) => {
        setParsed(result);
        const first =
          result.sheets.find((item) => item.rowCount > 0) ?? result.sheets[0];
        if (first) applySheet(first.name, result);
      },
    });
  };

  const request = () => ({
    rows: mappedRows(sheet?.rows ?? [], mapping),
    mapping,
    ...(constituency ? { constituency } : {}),
  });

  const runPreview = () => {
    if (!sheet) return;
    previewImport.mutate(request(), {
      onSuccess: (result) => {
        setPreview(result);
        setStep('review');
      },
    });
  };

  const runImport = () => {
    commit.mutate(request(), {
      onSuccess: (result) => {
        toast.success(
          `Imported ${result.created} new and updated ${result.updated} ${
            result.created + result.updated === 1 ? noun : nounPlural
          }.`
        );
        close(false);
      },
    });
  };

  const setField = (field: string, header: string) => {
    setMapping((prev) => {
      const next = { ...prev };
      if (header === NOT_USED) delete next[field];
      else next[field] = header;
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="dark tw:max-h-[90vh] tw:!max-w-3xl tw:overflow-y-auto tw:bg-background tw:text-foreground">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {step === 'upload'
              ? 'Choose a spreadsheet'
              : step === 'map'
                ? 'Match columns to fields'
                : 'Review and import'}
          </DialogDescription>
        </DialogHeader>

        {step === 'upload' && (
          <div className="tw:space-y-3">
            {uploadExtra}
            <label
              className="tw:flex tw:cursor-pointer tw:flex-col tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-dashed tw:border-border tw:px-4 tw:py-8 tw:text-center tw:text-sm tw:hover:border-ring"
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                chooseFile(event.dataTransfer.files?.[0]);
              }}
            >
              <FileSpreadsheet className="tw:size-6 tw:text-muted-foreground" />
              <span>
                {file
                  ? file.name
                  : 'Drop an .xlsx, .xls or .csv file here, or choose one'}
              </span>
              <span className="tw:text-[12px] tw:text-muted-foreground">
                One {noun} per row, up to 1,000 rows and 10 MB.
              </span>
              <input
                type="file"
                accept={ACCEPT}
                className="tw:sr-only"
                aria-label="Spreadsheet file"
                onChange={(event) => chooseFile(event.target.files?.[0])}
              />
            </label>
            <Button
              type="button"
              variant="link"
              size="sm"
              className="tw:px-0"
              onClick={() => void downloadDirectoryImportTemplate(kind)}
            >
              Download template
            </Button>
            {parse.isPending && (
              <p className="tw:text-sm tw:text-muted-foreground">
                Reading the file…
              </p>
            )}
            <ErrorText message={directoryImportError(parse.error)} />
            {parsed && parsed.sheets.length > 1 && (
              <Field>
                <FieldLabel htmlFor={`${kind}-import-sheet`}>Sheet</FieldLabel>
                <Select
                  value={sheetName}
                  onValueChange={(next) => applySheet(String(next))}
                >
                  <SelectTrigger
                    id={`${kind}-import-sheet`}
                    size="sm"
                    className="tw:w-full"
                  >
                    <SelectValue>{sheetName}</SelectValue>
                  </SelectTrigger>
                  <SelectContent className="dark">
                    {parsed.sheets.map((item) => (
                      <SelectItem key={item.name} value={item.name}>
                        {item.name} ({item.rowCount} rows)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}
            {sheet && (
              <p className="tw:text-sm tw:text-muted-foreground">
                {sheet.rowCount} row{sheet.rowCount === 1 ? '' : 's'} found.
              </p>
            )}
            {tooManyRows && (
              <ErrorText
                message={`This sheet has ${sheet!.rowCount} rows; the limit is ${parsed!.maxRows}. Split the file and import each part.`}
              />
            )}
          </div>
        )}

        {step === 'map' && sheet && (
          <div className="tw:space-y-4">
            <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-2">
              {fields.map((item) => (
                <Field key={item.field}>
                  <FieldLabel htmlFor={`${kind}-import-field-${item.field}`}>
                    {item.label}
                  </FieldLabel>
                  <Select
                    value={mapping[item.field] ?? NOT_USED}
                    onValueChange={(next) => setField(item.field, String(next))}
                  >
                    <SelectTrigger
                      id={`${kind}-import-field-${item.field}`}
                      size="sm"
                      className="tw:w-full"
                    >
                      <SelectValue>
                        {mapping[item.field] ?? 'Not used'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="dark">
                      <SelectItem value={NOT_USED}>Not used</SelectItem>
                      {sheet.headers.map((header) => (
                        <SelectItem key={header} value={header}>
                          {header}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              ))}
            </div>
            {mappedFields.length > 0 && sheet.rows.length > 0 && (
              <div className="tw:overflow-x-auto tw:rounded-md tw:border tw:border-border">
                <table className="tw:w-full tw:border-collapse tw:text-[12px]">
                  <thead className="tw:bg-muted">
                    <tr className="tw:text-left tw:text-muted-foreground">
                      {mappedFields.map((item) => (
                        <th
                          key={item.field}
                          className="tw:px-2 tw:py-1.5 tw:font-medium"
                        >
                          {item.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sheet.rows.slice(0, SAMPLE_ROWS).map((row, index) => (
                      <tr key={index} className="tw:border-t tw:border-border">
                        {mappedFields.map((item) => (
                          <td key={item.field} className="tw:px-2 tw:py-1.5">
                            {String(row[mapping[item.field]!] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <ErrorText
              message={
                mappingReady
                  ? null
                  : `Match at least one of: ${fields
                      .filter((item) => requiredAnyOf.includes(item.field))
                      .map((item) => item.label)
                      .join(', ')}.`
              }
              muted
            />
            <ErrorText message={directoryImportError(previewImport.error)} />
          </div>
        )}

        {step === 'review' && preview && (
          <div className="tw:space-y-3">
            <div className="tw:flex tw:flex-wrap tw:gap-2 tw:text-sm">
              {(Object.keys(STATUS_LABELS) as DirectoryImportStatus[]).map(
                (status) => (
                  <Badge key={status} variant={STATUS_VARIANT[status]}>
                    {STATUS_LABELS[status]}: {preview.counts[status]}
                  </Badge>
                )
              )}
            </div>
            <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
              Updates fill in the cells that have values; blank cells keep what
              is saved. Invalid rows and repeats are skipped.
            </p>
            <div className="tw:max-h-80 tw:overflow-auto tw:rounded-md tw:border tw:border-border">
              <table className="tw:w-full tw:border-collapse tw:text-[12px]">
                <thead className="tw:sticky tw:top-0 tw:bg-muted">
                  <tr className="tw:text-left tw:text-muted-foreground">
                    <th className="tw:px-2 tw:py-1.5 tw:font-medium">Row</th>
                    <th className="tw:px-2 tw:py-1.5 tw:font-medium">Status</th>
                    <th className="tw:px-2 tw:py-1.5 tw:font-medium">
                      {noun.charAt(0).toUpperCase() + noun.slice(1)}
                    </th>
                    <th className="tw:px-2 tw:py-1.5 tw:font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row) => (
                    <tr
                      key={row.rowNumber}
                      className="tw:border-t tw:border-border"
                    >
                      <td className="tw:px-2 tw:py-1.5">{row.rowNumber}</td>
                      <td className="tw:px-2 tw:py-1.5">
                        <Badge variant={STATUS_VARIANT[row.status]}>
                          {STATUS_LABELS[row.status]}
                        </Badge>
                      </td>
                      <td className="tw:px-2 tw:py-1.5">
                        {rowLabel(row.fields)}
                      </td>
                      <td className="tw:px-2 tw:py-1.5 tw:text-muted-foreground">
                        {row.errors.join(' ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ErrorText message={directoryImportError(commit.error)} />
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => close(false)}
          >
            Cancel
          </Button>
          {step !== 'upload' && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={previewImport.isPending || commit.isPending}
              onClick={() => setStep(step === 'review' ? 'map' : 'upload')}
            >
              Back
            </Button>
          )}
          {step === 'upload' && (
            <Button
              type="button"
              size="sm"
              disabled={!sheet || sheet.rowCount === 0 || tooManyRows}
              onClick={() => setStep('map')}
            >
              Next
            </Button>
          )}
          {step === 'map' && (
            <Button
              type="button"
              size="sm"
              disabled={!mappingReady || previewImport.isPending}
              onClick={runPreview}
            >
              {previewImport.isPending ? 'Checking…' : 'Preview import'}
            </Button>
          )}
          {step === 'review' && (
            <Button
              type="button"
              size="sm"
              disabled={importCount === 0 || commit.isPending}
              onClick={runImport}
            >
              {commit.isPending
                ? 'Importing…'
                : `Import ${importCount} ${importCount === 1 ? noun : nounPlural}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ErrorText({
  message,
  muted,
}: {
  message: string | null;
  muted?: boolean;
}) {
  if (!message) return null;
  return (
    <p
      role={muted ? undefined : 'alert'}
      className={`tw:text-sm ${muted ? 'tw:text-muted-foreground' : 'tw:text-destructive'}`}
    >
      {message}
    </p>
  );
}
